import Phaser from 'phaser';
import { TILE_SIZE, VIEWPORT_TILES } from '@blockworld/shared';
import { NetworkClient } from './network/NetworkClient';
import { GameScene } from './scenes/GameScene';

// ============================================================
// MAIN ENTRY POINT
// ============================================================

// Get server URL from environment or default
const SERVER_URL = import.meta.env.VITE_SERVER_URL || 'ws://localhost:2567';

// UI Elements
const connectionStatus = document.getElementById('connection-status')!;
const playerCount = document.getElementById('player-count')!;

// Create network client
const networkClient = new NetworkClient(SERVER_URL);

// Setup connection status callbacks
networkClient.onConnectionChange = (status) => {
    connectionStatus.className = status === 'connected' ? 'connected' : 'disconnected';
    switch (status) {
        case 'connecting':
            connectionStatus.textContent = 'Connecting...';
            break;
        case 'connected':
            connectionStatus.textContent = 'Connected ✓';
            break;
        case 'disconnected':
            connectionStatus.textContent = 'Disconnected';
            break;
        case 'error':
            connectionStatus.textContent = 'Connection Error';
            break;
    }
};

networkClient.onPlayerCountChange = (count) => {
    playerCount.textContent = `Players: ${count}`;
};

// Native game resolution
const GAME_WIDTH = VIEWPORT_TILES * TILE_SIZE;
const GAME_HEIGHT = VIEWPORT_TILES * TILE_SIZE;
const BUFFER = 40; // Buffer from window edges

// Calculate scale to fit window
function calculateScale(): number {
    const maxWidth = window.innerWidth - BUFFER;
    const maxHeight = window.innerHeight - BUFFER;
    
    // Calculate integer scale that fits
    const scaleX = Math.floor(maxWidth / GAME_WIDTH);
    const scaleY = Math.floor(maxHeight / GAME_HEIGHT);
    
    // Use the smaller scale to ensure it fits both dimensions
    // Minimum scale of 1
    return Math.max(1, Math.min(scaleX, scaleY));
}

// Create Phaser game config
const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    width: GAME_WIDTH,
    height: GAME_HEIGHT,
    parent: 'game-container',
    backgroundColor: '#87CEEB',
    pixelArt: true,
    render: {
        antialias: false,
        pixelArt: true,
        roundPixels: true,
    },
    scale: {
        mode: Phaser.Scale.NONE, // We handle scaling manually
    },
    scene: new GameScene(networkClient),
};

// Initialize game
const game = new Phaser.Game(config);

// Apply initial scale
function applyScale() {
    const scale = calculateScale();
    const canvas = game.canvas;
    if (canvas) {
        canvas.style.width = `${GAME_WIDTH * scale}px`;
        canvas.style.height = `${GAME_HEIGHT * scale}px`;
        canvas.style.imageRendering = 'pixelated';
        canvas.style.imageRendering = 'crisp-edges';
    }
}

// Apply scale after game is ready
game.events.once('ready', applyScale);

// Re-apply scale on window resize
window.addEventListener('resize', applyScale);

// Connect to server
networkClient.connect().catch((error) => {
    console.error('Failed to connect to server:', error);
    connectionStatus.textContent = 'Failed to connect. Is the server running?';
    connectionStatus.className = 'disconnected';
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    networkClient.disconnect();
});
